// set of protected feature switches not allowed to be overwritten by a cookie
const PROTECTED_FS_SET = new Set(['allowSTHUndeliveredReturns']);

// Checks if the current environment is not SSR.
const getFeatureSwitchWindow = (): Window | undefined => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

/**
 * Checks if a cookie exists with the specified key.
 *
 * @param {string} key - The name of the cookie to check.
 * @returns {string | undefined} Value of cookie, otherwise undefined if cookie does not exist
 */
const getFeatureSwitchCookieValue = (key: string) => {
  if (!key) return undefined;
  // Keep this local so feature-switch resolution does not import cookie-helper -> general.
  const cookies = getFeatureSwitchWindow()?.document?.cookie.split(';');

  // Use the find method to check if any cookie starts with the given name
  const cookie = cookies?.find((cookieVal) => {
    const trimmedCookie = cookieVal.trim();
    return trimmedCookie.startsWith(`${key}=`);
  });

  if (cookie) {
    // Extract the value after the '=' character
    const value = cookie?.split('=')?.[1];
    return value.trim();
  }

  return undefined;
};

/**
 * Helper function used to determine if FS is turned on, FS can be overwritten via cookie
 * @param featureSwitchName FS Name
 * @returns true if FS is set to true or 1
 */

// Used when instance variable is not available in non-hooks
export const getFeatureSwitchValueStatic = (featureSwitchName = '') => {
  const windowObj = getFeatureSwitchWindow();
  if (!windowObj) return 'false';
  const isProd = windowObj.location.hostname === 'www.homedepot.com';
  const isFSProtected = isProd && PROTECTED_FS_SET.has(featureSwitchName);

  if (!isFSProtected) {
    // Check cookies first
    const cookieValue = getFeatureSwitchCookieValue(featureSwitchName);
    if (cookieValue !== undefined) {
      return cookieValue;
    }
  }

  const featureSwitchValue =
    windowObj.__EXPERIENCE_PROPS__?.expConfiguration?.[featureSwitchName];
  return featureSwitchValue;
};

/**
 * Get the value of a feature switch from experience.
 */
export const getFeatureSwitchValue = (
  fsName: string,
  isCookieOverwriteable?: boolean,
): boolean | string | number | null => {
  let selectedFs =
    getFeatureSwitchWindow()?.__EXPERIENCE_PROPS__?.expConfiguration[fsName];

  if (isCookieOverwriteable) {
    selectedFs = getFeatureSwitchValueStatic(fsName);
  }

  // Return null if the feature switch is not found
  if (selectedFs === undefined || selectedFs === null) {
    return null;
  }
  // Checks if the feature switch is a boolean then returns the value as a boolean
  if ((selectedFs as string).toLocaleLowerCase() === 'true') {
    return true;
  }
  if ((selectedFs as string).toLocaleLowerCase() === 'false') {
    return false;
  }
  // Checks if the feature switch is a number then returns the value as a number
  if (!Number.isNaN(Number(selectedFs))) {
    return Number(selectedFs);
  }
  // Returns the value as a string
  return selectedFs;
};
